import { createAsyncThunk } from '@reduxjs/toolkit';

import { authService } from '../../services/auth';
import { AuthService } from '../../services/auth/authService';
import { AuthSession } from '../../types';

/**
 * Creates auth async thunks with dependency injection
 * This avoids the singleton pattern by allowing the AuthService to be injected
 */
export const createAuthThunks = (authService: AuthService) => ({
  initializeAuth: createAsyncThunk<
    { session: AuthSession | null },
    void,
    { rejectValue: string }
  >('auth/initialize', async (_, { rejectWithValue }) => {
    try {
      const session = await authService.getCurrentSession();
      return { session };
    } catch {
      return rejectWithValue('Failed to initialize authentication');
    }
  }),

  checkUserGoals: createAsyncThunk<boolean, void, { rejectValue: string }>(
    'auth/checkGoals',
    async (_, { rejectWithValue }) => {
      try {
        const result = (await authService.hasGoals()) as unknown as {
          hasGoals: boolean;
          error?: { message?: string };
        };
        if (result.error) {
          return rejectWithValue(result.error.message || 'Unknown error');
        }
        return result.hasGoals;
      } catch {
        return rejectWithValue('Failed to check user goals');
      }
    }
  ),

  checkOnboardingComplete: createAsyncThunk<
    boolean,
    void,
    { rejectValue: string }
  >('auth/checkOnboarding', async (_, { rejectWithValue }) => {
    try {
      const isComplete = await authService.isOnboardingComplete();
      return isComplete;
    } catch {
      return rejectWithValue('Failed to check onboarding status');
    }
  }),

  checkCoreOnboardingComplete: createAsyncThunk<
    boolean,
    void,
    { rejectValue: string }
  >('auth/checkCoreOnboarding', async (_, { rejectWithValue }) => {
    try {
      const isComplete = await authService.isCoreOnboardingComplete();
      return isComplete;
    } catch {
      return rejectWithValue('Failed to check core onboarding status');
    }
  }),

  signOut: createAsyncThunk<boolean, void, { rejectValue: string }>(
    'auth/signOut',
    async (_, { rejectWithValue }) => {
      try {
        const result = await authService.signOut();
        if (result.error) {
          return rejectWithValue(result.error?.message || 'Unknown error');
        }
        return true;
      } catch {
        return rejectWithValue('Failed to sign out');
      }
    }
  ),
});

/**
 * Default exports using createAuthThunks factory
 * These can be used directly in web/mobile apps that have already configured their authService
 * Note: This requires authService to be imported from the platform-specific package
 */
const defaultThunks = createAuthThunks(authService);

export const initializeAuth = defaultThunks.initializeAuth;
export const checkUserGoals = defaultThunks.checkUserGoals;
export const checkOnboardingComplete = defaultThunks.checkOnboardingComplete;
export const checkCoreOnboardingComplete =
  defaultThunks.checkCoreOnboardingComplete;
export const signOut = defaultThunks.signOut;
